import { useState, useEffect, useCallback, useRef } from 'react';

interface UseInfiniteScrollOptions {
  data: any[];
  initialItemsPerLoad?: number;
  threshold?: number;
}

export const useInfiniteScroll = ({
  data,
  initialItemsPerLoad = 20,
  threshold = 100
}: UseInfiniteScrollOptions) => {
  const [displayedItems, setDisplayedItems] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);

  // Reset when data changes
  useEffect(() => {
    const initialItems = data.slice(0, initialItemsPerLoad);
    setDisplayedItems(initialItems);
    setHasMore(initialItems.length < data.length);
    loadingRef.current = false;
  }, [data, initialItemsPerLoad]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMore) return;
    
    loadingRef.current = true;
    
    setTimeout(() => {
      setDisplayedItems(prev => {
        const nextItems = data.slice(0, prev.length + initialItemsPerLoad);
        setHasMore(nextItems.length < data.length);
        loadingRef.current = false;
        return nextItems;
      });
    }, 100);
  }, [data, initialItemsPerLoad, hasMore]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    
    if (scrollHeight - scrollTop - clientHeight < threshold && hasMore && !loadingRef.current) {
      loadMore();
    }
  }, [threshold, hasMore, loadMore]);

  return {
    displayedItems,
    hasMore,
    isLoading: loadingRef.current,
    handleScroll
  };
};